import { Link } from '@tanstack/react-router'
import type { InvoiceResponseDto } from '@trellis/api'
import { RowActions } from '@trellis/roots/data'
import type { RowAction } from '@trellis/roots/data'
import {
  Archive,
  ArchiveRestore,
  Check,
  Copy,
  Edit,
  Eye,
  RefreshCw,
  Trash2,
} from 'lucide-react'

import type { InvoiceWithRecurring } from '@/types/invoice'

export function InvoiceRowActionsMenu({
  invoice,
  onDeleteClick,
  onDuplicateClick,
  onReissueClick,
  onPreviewClick,
  onArchiveClick,
  onUnarchiveClick,
  onMarkSentClick,
  onMarkPaidClick,
}: {
  invoice: InvoiceResponseDto
  onDeleteClick: (invoice: InvoiceWithRecurring) => void
  onDuplicateClick: (invoice: InvoiceWithRecurring) => void
  onReissueClick: (invoice: InvoiceWithRecurring) => void
  onPreviewClick: (invoice: InvoiceWithRecurring) => void
  onArchiveClick: (invoice: InvoiceWithRecurring) => void
  onUnarchiveClick: (invoice: InvoiceWithRecurring) => void
  onMarkSentClick: (invoice: InvoiceWithRecurring) => void
  onMarkPaidClick: (invoice: InvoiceWithRecurring) => void
}) {
  const isDraft = invoice.status === 'DRAFT'
  const isSentOrOverdue = invoice.status === 'SENT' || invoice.status === 'OVERDUE'
  const isReissuable = invoice.status === 'OVERDUE' || invoice.status === 'CANCELLED'
  const isArchived = !!invoice.archivedAt
  const actions: RowAction[] = [
    {
      label: 'View',
      icon: <Eye className="mr-2 h-4 w-4" />,
      href: `/invoices/${invoice.id}`,
    },
    {
      label: 'Duplicate',
      icon: <Copy className="mr-2 h-4 w-4" />,
      onClick: () => onDuplicateClick(invoice as InvoiceWithRecurring),
    },
    ...(isReissuable
      ? [
          {
            label: 'Re-issue',
            icon: <RefreshCw className="mr-2 h-4 w-4" />,
            onClick: () => onReissueClick(invoice as InvoiceWithRecurring),
          } satisfies RowAction,
        ]
      : []),
    ...(isDraft
      ? [
          {
            label: 'Edit',
            icon: <Edit className="mr-2 h-4 w-4" />,
            href: `/invoices/${invoice.id}/edit`,
          } satisfies RowAction,
        ]
      : []),
    ...(isDraft
      ? [
          {
            label: 'Mark as sent',
            icon: <Check className="mr-2 h-4 w-4" />,
            onClick: () => onMarkSentClick(invoice as InvoiceWithRecurring),
            separator: true,
          } satisfies RowAction,
        ]
      : []),
    ...(isSentOrOverdue
      ? [
          {
            label: 'Mark as paid',
            icon: <Check className="mr-2 h-4 w-4" />,
            onClick: () => onMarkPaidClick(invoice as InvoiceWithRecurring),
            separator: true,
          } satisfies RowAction,
        ]
      : []),
    ...(isArchived
      ? [
          {
            label: 'Unarchive',
            icon: <ArchiveRestore className="mr-2 h-4 w-4" />,
            onClick: () => onUnarchiveClick(invoice as InvoiceWithRecurring),
            separator: true,
          } satisfies RowAction,
        ]
      : [
          {
            label: 'Archive',
            icon: <Archive className="mr-2 h-4 w-4" />,
            onClick: () => onArchiveClick(invoice as InvoiceWithRecurring),
            separator: true,
          } satisfies RowAction,
        ]),
    ...(isDraft
      ? [
          {
            label: 'Delete',
            icon: <Trash2 className="mr-2 h-4 w-4" />,
            onClick: () => onDeleteClick(invoice as InvoiceWithRecurring),
            destructive: true,
            separator: true,
          } satisfies RowAction,
        ]
      : []),
  ]

  return (
    <RowActions
      onViewClick={() => onPreviewClick(invoice as InvoiceWithRecurring)}
      triggerLabel={`Open actions menu for ${invoice.invoiceNumber}`}
      actions={actions}
      linkComponent={Link}
    />
  )
}
